import { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { FormField, FormHelp, Label, Textarea, TextButton } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../../hooks';
import { useSelector } from '../../../../selectors/useSelector';
import { postingConstraints } from '../../../../utils/formValidation';
import { multiFieldsUtil } from '../../../../utils/postingUtils';

import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { RECS } from '../../../../constants/RecommendationsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import WizardServerError from '../../../../model/WizardServerError';

import PropertyDescriptionStrength from '../../../../components/postingPath/PropertyDescriptionStrength';
import RecommendationsLearnMoreContent from '../../../../components/RecommendationsLearnMoreContent';
import { WizardContext } from '../../../../components/Wizard/context';
import WizardAlert from '../../../../components/WizardAlert';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import type { ChangeEvent } from 'react';

const apiFieldName = 'fullDescription';

const DESCRIPTION = 'description';
const ROOM_DESCRIPTION = 'roomDescription';
const CURRENT_PROPERTY_DESCRIPTION_MAP = {
  [DESCRIPTION]: DESCRIPTION,
  [ROOM_DESCRIPTION]: DESCRIPTION,
} as const;

type Props = {
  isRelistingFieldset?: boolean;
};

const PropertyDescriptionFormField = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { listingAlias, description, currentProperty } = useSelector(
    (state) => ({
      listingAlias: state.properties.currentProperty.listingAlias,
      description: state.properties.currentProperty.description,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const inputName = isRoomForRent ? ROOM_DESCRIPTION : DESCRIPTION;

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      [inputName]: description,
    },
    postingConstraints,
  );

  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  const label = isRoomForRent ? 'Room and shared space description' : 'Property description';
  const isRequired = isRoomForRent ? true : false;

  // @ts-expect-error
  const displayFieldError = isRoomForRent ? !!errorObj?.[inputName] : false;

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error inputs?.[inputName
          value: inputs?.[inputName],
        },
      });
    }
  }, [isSaveComplete]);

  const displayError = serverErrorObj.fieldName === inputName && serverErrorObj.message !== '';

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const dataToSave = {
      property: {
        [apiFieldName]: value,
        listingAlias,
      },
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        dispatch(
          PropertiesActions.updateCurrentProperty({
            [CURRENT_PROPERTY_DESCRIPTION_MAP[fieldName as keyof typeof CURRENT_PROPERTY_DESCRIPTION_MAP]]: value,
          }),
        );
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    // for discriminatory content we will let the form field handle displaying the error
    // index of 'comply' indicates a content violation
    const hasDiscriminatoryContent = message[0]?.toLowerCase().indexOf('comply') !== -1;

    // for relisting field set, show all errors in FormHelp instead of WizardAlert
    const showInFormHelp = isRelistingFieldset || hasDiscriminatoryContent;

    // for WizardAlert
    const displayMessage = showInFormHelp ? '' : message;

    // for FormHelp
    message = showInFormHelp ? message : '';

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage,
      }),
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  const renderTextAreaPlaceholder = () => {
    if (isRoomForRent) {
      return 'Example: Private room with large windows and a view of the park. Recently renovated kitchen and bathrooms.';
    }

    return 'Example: Freshly painted home with new appliances and carpeting. Easy walking to public transit and a great neighborhood.';
  };

  return (
    <div data-id="PropertyDescriptionFormField">
      {!isRelistingFieldset && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <FormField
        error={displayFieldError}
        // @ts-expect-error
        description={displayFieldError && <FormHelp error={displayFieldError}>{errorObj?.[inputName]}</FormHelp>}
      >
        <Label required={isRequired}>
          {label}
          {isRelistingFieldset && (
            <TextButton
              css={{ paddingLeft: '200' }}
              textStyle="body-sm-bold"
              onClick={() => {
                setDisplayRecsModal(true);
              }}
            >
              Learn more
            </TextButton>
          )}
        </Label>
        <Textarea
          name={inputName}
          value={inputs?.[inputName] as string}
          placeholder={renderTextAreaPlaceholder()}
          rows={10}
          onChange={handleChange}
          onBlur={save}
          error={displayError || displayFieldError}
          data-testid="PropertyDescriptionFormField-Textarea"
        />
        {displayError && (
          <FormHelp error={displayError}>
            <span dangerouslySetInnerHTML={{ __html: serverErrorObj.message }} />
          </FormHelp>
        )}
        {Boolean(!displayFieldError && inputs?.[inputName]) && (
          <PropertyDescriptionStrength description={inputs?.[inputName] as string} />
        )}
      </FormField>
      {isRelistingFieldset && displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={RECS?.DESCRIPTION}
          isFromRelistingFlow={true}
        />
      )}
    </div>
  );
};

export default PropertyDescriptionFormField;
